'use client'
import { useEffect, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import dynamic from 'next/dynamic'

import { RaButton, RaHeading } from '@components/core/index.core'
import { ICONS } from '@constants/icons.constants'
import { useUi } from '@contexts/ui.context'
import { useOutsideClick } from '@utilities/hooks/useOutsideClick.hooks'

import './ra-modal.styles.scss'

// Toaster needs to be imported here as it might otherwise not be shown on fullscreen modals
const RaToaster = dynamic(() =>
  import('@components/features/RaToaster/RaToaster').then(
    ({ RaToaster }) => RaToaster
  )
)

export const RaModal = () => {
  const dialogRef = useRef(null)
  const { modal, closeModal } = useUi()
  const [scrollPosition, setScrollPosition] = useState(0)

  const contentRef = useOutsideClick(() => {
    // If we allow outside click on fullscreen modals, the modal will close on scroll bar click
    modal.isOpen && !modal.fullscreen && closeModal()
  })

  const handleCloseListener = () => {
    if (modal.isOpen) {
      closeModal()
    }
  }

  const classes = useMemo(() => {
    return clsx('ra-modal', modal.fullscreen && 'fullscreen')
  }, [modal.fullscreen])

  useEffect(() => {
    if (modal.isOpen && dialogRef.current) {
      const scrollY = window.scrollY
      setScrollPosition(scrollY)
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      dialogRef.current.showModal()
    } else {
      document.body.style.position = 'initial'
      document.body.style.top = ''
      window.scrollTo(0, scrollPosition)
      dialogRef.current.close()
    }
  }, [modal.isOpen])

  useEffect(() => {
    if (dialogRef?.current) {
      dialogRef.current.addEventListener('close', handleCloseListener)
    }
    return () =>
      dialogRef?.current?.removeEventListener('close', handleCloseListener)
  }, [dialogRef?.current])

  return (
    <dialog
      ref={dialogRef}
      className={classes}
      style={
        {
          '--modal-background': modal.bgColor
            ? modal.bgColor
            : 'var(--color-white)',
          maxWidth: modal.maxWidth || 'auto',
        } as React.CSSProperties
      }>
      <div className={'ra-modal__header'}>
        {!modal.title ? (
          <div></div>
        ) : (
          <RaHeading
            component="h3"
            noPadding>
            {modal.title}
          </RaHeading>
        )}
        {!modal.customCloseButton && !modal.isMenu && (
          <RaButton
            variant="icon"
            textColor={modal.isMenu ? 'light' : 'primary'}
            icon={ICONS.CLOSE_ICON}
            onClick={closeModal}
          />
        )}
      </div>
      <div
        ref={contentRef}
        className={'ra-modal__content'}>
        {modal.content && (
          <div className={'ra-modal__body'}>{modal.content}</div>
        )}
      </div>
      {modal.fullscreen && <RaToaster />}
    </dialog>
  )
}
